// Rain, snow, and what drifts down in spring and autumn (2026-10-26; Sheep Tag gameview.ts makePrecip and makeWeather,
// sized for this camera). Two draws at most, each only while it has something to show: rain as line segments (only
// the streak's pixels are shaded, so a downpour stays cheap) and one set of points for snowflakes, falling leaves or
// blossom petals. Each drop has a fixed spot in a box anchored to the world, wrapped round a point between the camera and
// what it looks at, so panning never drags the weather along; the spots come from a hash (no Math.random), and the drops
// are ordered so the sky's amount is a prefix: drawRange draws that many and the GPU never touches the rest. Light draws
// LIGHT_SHARE of what Best does.
import * as THREE from 'three';
import { hash3 } from './detail';
import { shared, type Tier } from './look';
import { PALETTE } from './palette';
import type { SkyState } from './sky';

/** Drops at full rain and flakes at a full snowfall on Best and Ultra; leaves and petals use the flakes. */
export const PRECIP = { rain: 9000, flakes: 7000 };
export const LIGHT_SHARE = 0.4;
/** The box the drops fall through, meters: W across, H tall. */
export const BOX = { w: 150, h: 110 };

/** n drops' spots in the unit box and their own draw (x, y, z, seed), the same every load. */
export function dropSeeds(n: number, salt: number) {
  const a = new Float32Array(n * 4);
  for (let k = 0; k < n; k++) for (let c = 0; c < 4; c++) a[k * 4 + c] = hash3(k, salt, 31 + c);
  return a;
}

const FALL = `
  uniform float uTime; uniform vec3 uCenter; uniform float uWindX; uniform float uFall;
  // a drop's spot at this time: falling, blown downwind, wrapped round the center in world space
  vec3 fallAt(vec4 a, float drift) {
    vec3 box = vec3(${BOX.w.toFixed(1)}, ${BOX.h.toFixed(1)}, ${BOX.w.toFixed(1)});
    vec3 p = a.xyz * box;
    float fall = uFall * (0.75 + a.w * 0.5);
    float base = uCenter.y - box.y * 0.5;
    p.y = mod(p.y - uTime * fall - base, box.y);
    p.x += uWindX * (box.y - p.y) * mix(0.12, 0.5, drift) + uTime * uWindX * 0.8 * drift;
    p.x += drift * sin(uTime * (0.6 + a.w) + a.w * 40.0) * 1.2;
    p.z += drift * cos(uTime * (0.5 + a.w) + a.w * 17.0) * 1.2;
    p.x = mod(p.x - uCenter.x + box.x * 0.5, box.x) - box.x * 0.5 + uCenter.x;
    p.z = mod(p.z - uCenter.z + box.z * 0.5, box.z) - box.z * 0.5 + uCenter.z;
    p.y += base;
    return p;
  }`;

export class Precip {
  readonly rain: THREE.LineSegments;
  readonly flakes: THREE.Points;
  private u = { uTime: shared.uTime, uCenter: { value: new THREE.Vector3() }, uWindX: { value: 0.4 }, uFall: { value: 1.8 }, uScale: { value: 400 }, uLight: { value: 1 }, uKind: { value: 0 },
    uLeafA: { value: PALETTE.foliage.oak[0] }, uLeafB: { value: PALETTE.foliage.oak[1] }, uPetal: { value: PALETTE.accent.oak } };

  constructor() {
    // rain: two vertices a drop, the tail trailing up and upwind
    const r = dropSeeds(PRECIP.rain, 1), seeds = new Float32Array(PRECIP.rain * 8), tail = new Float32Array(PRECIP.rain * 2);
    for (let k = 0; k < PRECIP.rain; k++) for (let j = 0; j < 2; j++) { seeds.set(r.subarray(k * 4, k * 4 + 4), (k * 2 + j) * 4); tail[k * 2 + j] = j; }
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(PRECIP.rain * 6), 3)); // unused: the spot is aDrop
    lg.setAttribute('aDrop', new THREE.BufferAttribute(seeds, 4));
    lg.setAttribute('aTail', new THREE.BufferAttribute(tail, 1));
    this.rain = new THREE.LineSegments(lg, new THREE.ShaderMaterial({
      uniforms: { ...this.u, uFall: { value: 20 } }, transparent: true, depthWrite: false, fog: false,
      vertexShader: `attribute vec4 aDrop; attribute float aTail; uniform float uLight; varying float vA; ${FALL}
        void main() {
          vec3 p = fallAt(aDrop, 0.0);
          float len = 1.3 + aDrop.w * 0.9;
          p.y += aTail * len; p.x -= aTail * len * uWindX * 0.25;
          vec4 mv = viewMatrix * vec4(p, 1.0);
          // fades in with distance: drops just past the lens were long white scratches over the far view
          vA = (1.0 - aTail * 0.85) * smoothstep(6.0, 30.0, -mv.z);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `uniform float uLight; varying float vA;
        void main() { gl_FragColor = vec4(mix(vec3(0.5, 0.56, 0.68), vec3(0.86, 0.9, 0.98), uLight), vA * 0.5); }`,
    }));
    // snow, leaves and petals: round points, or a turning leaf
    const f = dropSeeds(PRECIP.flakes, 2), pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(PRECIP.flakes * 3), 3));
    pg.setAttribute('aDrop', new THREE.BufferAttribute(f, 4));
    this.flakes = new THREE.Points(pg, new THREE.ShaderMaterial({
      uniforms: this.u, transparent: true, depthWrite: false, fog: false,
      vertexShader: `attribute vec4 aDrop; uniform float uScale; uniform float uKind; varying float vSeed; varying float vTurn; ${FALL}
        void main() {
          vec4 mv = viewMatrix * vec4(fallAt(aDrop, 1.0), 1.0);
          vSeed = aDrop.w; vTurn = uTime * (1.0 + aDrop.w * 3.0) + aDrop.w * 30.0;
          // meters across: a flake 0.3, a leaf or petal 0.45 (the toy soldiers stand about 2 m)
          gl_PointSize = min((uKind > 0.5 ? 0.45 : 0.3) * (0.6 + aDrop.w * 0.8) * uScale / -mv.z, 40.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `uniform float uKind; uniform float uLight; uniform vec3 uLeafA, uLeafB, uPetal; varying float vSeed; varying float vTurn;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          if (uKind < 0.5) {
            float a = smoothstep(0.5, 0.15, length(d));
            if (a < 0.02) discard;
            gl_FragColor = vec4(vec3(0.96, 0.98, 1.0) * uLight, a * 0.9);
            return;
          }
          // a leaf (autumn) or a petal (spring), turning as it falls
          float c = cos(vTurn), s = sin(vTurn);
          vec2 r = vec2(c * d.x - s * d.y, s * d.x + c * d.y);
          if (abs(r.x) * 2.2 + r.y * r.y * 3.2 > 0.45) discard;
          vec3 col = uKind < 1.5 ? mix(uLeafA, uLeafB, vSeed) : uPetal * (0.9 + 0.2 * vSeed);
          gl_FragColor = vec4(col * uLight, 1.0);
        }`,
    }));
    for (const o of [this.rain, this.flakes]) { o.frustumCulled = false; o.renderOrder = 5; o.visible = false; o.userData.warm = true; }
  }

  /** Once a frame: how much falls (the sky's amounts, a share on Light, none with Settings, Weather effects Off, which
   *  the SkyState already carries), where the box sits, and how lit the drops are. */
  update(s: SkyState, camera: THREE.PerspectiveCamera, focus: THREE.Vector3, tier: Tier, bufferHeight: number) {
    const share = tier === 'light' ? LIGHT_SHARE : 1, u = this.u, { rain, snow, wind } = s.wx;
    const snowy = snow > 0.05, drift = snowy ? snow : s.drift;
    const nRain = Math.floor(PRECIP.rain * rain * share), nFlakes = Math.floor(PRECIP.flakes * (snowy ? snow : s.drift * 0.25) * share);
    this.rain.geometry.setDrawRange(0, nRain * 2); this.rain.visible = nRain > 0;
    this.flakes.geometry.setDrawRange(0, nFlakes); this.flakes.visible = nFlakes > 0 && drift > 0;
    // the season says what drifts: leaves in autumn, petals in spring (snow wins when it snows)
    u.uKind.value = snowy ? 0 : s.season === 'spring' ? 2 : 1;
    u.uFall.value = snowy ? 1.8 : 1.3;
    u.uLeafA.value = PALETTE.foliage.oak[0]; u.uLeafB.value = PALETTE.foliage.birch[1]; u.uPetal.value = PALETTE.accent.oak;
    u.uWindX.value = 0.15 + wind * 0.6;
    u.uCenter.value.copy(focus).lerp(camera.position, 0.4);
    u.uScale.value = bufferHeight / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
    const sky = PALETTE.fill.sky, l = sky.r * 0.3 + sky.g * 0.59 + sky.b * 0.11;
    u.uLight.value = Math.min(1, Math.max(0.55, l * 1.2 * Math.min(1, PALETTE.fill.intensity)));
  }
}
